import { useEffect, useRef } from 'react';

import styles from './AppPreview.module.css';
import preview from '../../../../shared/assets/illustrations/app-preview.png';

// Fifty-one steps so the browser reports the ratio continuously instead of
// firing once at a single crossing point.
const STEPS = Array.from({ length: 51 }, (_, i) => i / 50);

// Hidden below this share of the image being on screen, fully there at the
// upper one – so it arrives late and starts fading the moment it begins to
// leave, rather than holding on until the last sliver.
const FADE_IN_AT = 0.4;
const FULLY_IN_AT = 0.8;

// A look at the product, sitting between Key Features and How It Works.
const AppPreview = () => {
	const frameRef = useRef(null);

	useEffect(() => {
		const node = frameRef.current;
		if (!node) return;

		const show = () => {
			node.style.opacity = '1';
			node.style.transform = 'none';
		};

		const reduced =
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

		if (reduced || typeof IntersectionObserver === 'undefined') {
			show();
			return;
		}

		// The image starts hidden in CSS, so anything that keeps the observer from
		// reporting (a lazily loaded image that has not sized itself yet on some
		// mobile browsers) would leave a blank gap for good. Observing always
		// delivers a first callback, so this only fires when it does not.
		const fallback = setTimeout(show, 1200);

		const observer = new IntersectionObserver(
			([entry]) => {
				clearTimeout(fallback);
				// In a window shorter than the image the ratio can never reach 1, so
				// both marks are scaled to what is actually reachable.
				const root = entry.rootBounds;
				const height = entry.boundingClientRect.height;
				const reach = root && height ? Math.min(1, root.height / height) : 1;
				const from = FADE_IN_AT * reach;
				const to = FULLY_IN_AT * reach;
				const span = to - from || 1;
				const progress = Math.min(
					Math.max((entry.intersectionRatio - from) / span, 0),
					1,
				);
				node.style.opacity = String(progress);
				node.style.transform = `translateY(${(1 - progress) * 26}px) scale(${
					0.985 + progress * 0.015
				})`;
			},
			{ threshold: STEPS },
		);

		observer.observe(node);
		return () => {
			clearTimeout(fallback);
			observer.disconnect();
		};
	}, []);

	return (
		<section className={styles.section}>
			<div ref={frameRef} className={styles.frame}>
				<img src={preview} alt='' loading='lazy' width='1451' height='1035' />
			</div>
		</section>
	);
};

export default AppPreview;
